import { type IDataObjectsState, REMOVE_DATA_OBJECTS, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { type AccessControlManager, type IDataObjectAccessControl, type IPadmeAce } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

export const UPDATE_FUNCTIONAL_OWNER = 'updateFunctionalOwner';
export const ADD_USERS_GROUPS = 'addUsersAndGroups';
export const REMOVE_GROUPS = 'removeGroups';
export const REMOVE_USERS = 'removeUsers';
export const MAKE_PUBLIC = 'makePublic';
export const MAKE_PRIVATE = 'makePrivate';
export const REMOVE_FROM_STORE = 'removeFromStore';

export function getActions<T extends IDataObjectAccessControl>(manager: AccessControlManager<T>): ActionTree<IDataObjectsState<T>, unknown> {
    return {
        async [UPDATE_FUNCTIONAL_OWNER](context, { dobjId, ownerId, properties }: { dobjId?: number; ownerId?: number; properties?: string }): Promise<void> {
            const result: T = await manager.updateFunctionalOwner(dobjId, ownerId, { properties });
            void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        },
        async [ADD_USERS_GROUPS](
            context,
            { objectId, groupIds, userIds, properties }: { objectId: number; groupIds?: number[]; userIds?: IPadmeAce[]; properties?: string }
        ) {
            let result;
            if ((groupIds.length ?? 0) > 0) {
                result = await manager.addGroups(objectId, groupIds, { properties });
            }

            if ((userIds.length ?? 0) > 0) {
                result = await manager.addUsers(objectId, userIds, { properties });
            }

            if (result !== undefined) {
                void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            }
        },
        async [REMOVE_GROUPS](context, { objectId, groupIds, properties }: { objectId: number; groupIds: number[]; properties?: string }) {
            const result = await manager.removeGroups(objectId, groupIds, { properties });

            if (result !== undefined) {
                void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            }
        },
        async [REMOVE_USERS](context, { objectId, userIds, properties }: { objectId: number; userIds: number[]; properties?: string }) {
            const result = await manager.removeUsers(objectId, userIds, { properties });

            if (result !== undefined) {
                void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            }
        },
        async [MAKE_PUBLIC](context, { objectId, properties }: { objectId: number; properties?: string }) {
            const result = await manager.makePublic(objectId, { properties });
            if (result !== undefined) {
                void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            }
        },
        async [MAKE_PRIVATE](
            context,
            { objectId, reassignLeadOwnerMap, properties }: { objectId: number; reassignLeadOwnerMap?: Map<number, number>; properties?: string }
        ) {
            const reassignLeadMap = reassignLeadOwnerMap ?? new Map();
            const result = await manager.makePrivate(objectId, reassignLeadMap, { properties });
            if (result !== undefined) {
                void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
            }
        },
        [REMOVE_FROM_STORE](context, { objectId }: { objectId: number }) {
            context.commit(REMOVE_DATA_OBJECTS, { dobjId: objectId });
        }
    };
}
